import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import { Camera, ClipboardList, Minus, Plus, Table2, UserRound, Users } from "lucide-react";

import "./AddGroup.css";
import "./EditGroup.css";
import {
  coachlist,
  playerlist,
  groupdetails,
  groupupdate,
  contenttypelist,
} from "../funtionsBD/Mainservice";
import { useLocation, useNavigate } from "react-router-dom";
import { PlayerSelectionDialog } from "./PlayerSelectionDialog";
import { GroupKindFields } from "./GroupKindFields";
import {
  GroupJoinRulesFields,
  emptyJoinRulesForm,
  joinRulesToForm,
  formToJoinRules,
} from "./GroupJoinRulesFields";
import { PrescriptionHealthFields } from "./PrescriptionHealthFields";
import {
  GroupLevelFields,
  emptyGroupLevel,
  groupLevelFromDetails,
} from "./GroupLevelFields";
import { capForGroupKind, normalizeGroupKind } from "../../utils/groupKind";

export const EditGroup = () => {
  const [pictures, setPictures] = useState();
  const [newImagePreviewUrl, setNewImagePreviewUrl] = useState(null);
  const [PLayers, setPlayers] = useState([]);
  const [Coach, setCoach] = useState([]);
  const [selectedtraining, setselectedtraining] = useState(null);
  const [success, setsuccess] = useState(false);
  const [open, setOpen] = useState(false);
  const [predata, setpredata] = useState();
  const [playerdata, setplayerdata] = useState();
  const [coachdata, setcoachdata] = useState();
  const [trainingModes, setTrainingModes] = useState([]);
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [numberOfTables, setNumberOfTables] = useState(1);
  const [savingPlayers, setSavingPlayers] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [joinRulesForm, setJoinRulesForm] = useState(emptyJoinRulesForm());
  const [levelForm, setLevelForm] = useState(emptyGroupLevel());
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state;
  const { t } = useTranslation();

  const id = data.id;

  useEffect(() => {
    getgroupdetails(id);
    loadTrainingModes();
  }, []);

  const loadTrainingModes = async () => {
    try {
      const response = await contenttypelist();
      if (response.data.status === "success") {
        setTrainingModes(response.data.data || []);
      } else {
        setTrainingModes([]);
      }
    } catch (error) {
      console.error("Error loading training modes:", error);
      setTrainingModes([]);
    }
  };

  const getuser = async (clubId) => {
    try {
      const response = await playerlist(clubId);
      setplayerdata(response.data.data);
      const response2 = await coachlist(clubId);
      setcoachdata(response2.data.data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const getgroupdetails = async (groupId) => {
    try {
      const response = await groupdetails(groupId);
      getuser(response.data.data.club_id);
      setpredata(response.data.data);
      setPlayers(
        response.data.data.tbl_players?.map((player) => player.player_id) || []
      );
      setCoach(response.data.data.tbl_coaches?.map((coach) => coach.coach_id) || []);
      formik.setValues({
        name: response.data.data.gr_name,
        des: response.data.data.gr_des,
        kind: normalizeGroupKind(response.data.data.gr_kind),
        guestCoachName: response.data.data.gr_guest_coach_name || "",
      });
      setJoinRulesForm(joinRulesToForm(response.data.data.gr_join_rules));
      setLevelForm(groupLevelFromDetails(response.data.data));

      const trainingMode =
        response.data.data.tbl_gen_mode_ts ||
        response.data.data.tbl_gen_mode_t ||
        null;

      let grTrainingSessionType = response.data.data.gr_traning_session_type;
      if (
        grTrainingSessionType === "undefined" ||
        grTrainingSessionType === "null" ||
        !grTrainingSessionType
      ) {
        grTrainingSessionType = null;
      }

      if (trainingMode && trainingMode.te_gen_mode_id) {
        setselectedtraining(trainingMode.te_gen_mode_id);
      } else if (grTrainingSessionType) {
        setselectedtraining(grTrainingSessionType);
      } else {
        setselectedtraining(null);
      }

      if (response.data.data.gr_number_of_tables) {
        setNumberOfTables(response.data.data.gr_number_of_tables);
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
    }
  };

  const closealert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    setPictures(file || undefined);
  };

  useEffect(() => {
    if (!pictures) {
      setNewImagePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(pictures);
    setNewImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pictures]);

  const buildPayload = (values) => ({
    name: values.name,
    des: values.des,
    kind: values.kind,
    guestCoachName: values.guestCoachName,
    joinRules: formToJoinRules(joinRulesForm),
    level: levelForm,
  });

  const persistGroupMembers = async (nextPlayers) => {
    const payload = buildPayload(formik.values);
    const cap = capForGroupKind(payload.kind);
    if (cap && nextPlayers.length > cap) {
      const message = t("groupKindCap", "At most {{cap}} players in this group.", { cap });
      setAlertSeverity("error");
      setsuccess(message);
      setOpen(true);
      throw new Error(message);
    }
    const response = await groupupdate(
      pictures,
      payload,
      selectedtraining,
      Coach,
      nextPlayers,
      id,
      numberOfTables
    );
    return response;
  };

  const handlePlayersSelected = async (selected) => {
    const nextPlayers = Array.isArray(selected) ? selected : [];
    const previousPlayers = PLayers;
    if (!id) {
      setPlayers(nextPlayers);
      return;
    }
    setSavingPlayers(true);
    setPlayers(nextPlayers);
    try {
      const response = await persistGroupMembers(nextPlayers);
      setAlertSeverity("success");
      setsuccess(
        response?.data?.message ||
          t("groupPlayersSaved") ||
          "Players updated."
      );
      setOpen(true);
      await getgroupdetails(id);
    } catch (error) {
      console.error("Error saving group players:", error);
      setPlayers(previousPlayers);
      setAlertSeverity("error");
      setsuccess(
        error?.message ||
          t("errorSavingGroupPlayers") ||
          "Could not update players."
      );
      setOpen(true);
      throw error;
    } finally {
      setSavingPlayers(false);
    }
  };

  const handleadd = async (values) => {
    const cap = capForGroupKind(values.kind);
    if (cap && PLayers.length > cap) {
      setAlertSeverity("error");
      setsuccess(t("groupKindCap", "At most {{cap}} players in this group.", { cap }));
      setOpen(true);
      return;
    }
    try {
      const response = await groupupdate(
        pictures,
        buildPayload(values),
        selectedtraining,
        Coach,
        PLayers,
        id,
        numberOfTables
      );
      setAlertSeverity("success");
      setsuccess(response.data.message);
      setOpen(true);
      setTimeout(() => {
        navigate("/GroupList");
      }, 1500);
    } catch (error) {
      console.error("Error updating group:", error);
      setAlertSeverity("error");
      setsuccess(
        error?.message || t("errorSavingGroup") || "Could not save group."
      );
      setOpen(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: predata?.gr_name,
      des: predata?.gr_des,
      kind: "training",
      guestCoachName: "",
    },
    onSubmit: (values) => {
      handleadd(values);
    },
  });

  const personName = (person) => {
    if (person?.user_first_name || person?.user_last_name) {
      return `${person.user_first_name || ""} ${person.user_last_name || ""}`.trim();
    }
    return person?.user_alias || t("player");
  };

  const personInitials = (person) => {
    if (person?.user_alias) return person.user_alias.slice(0, 2).toUpperCase();
    const first = person?.user_first_name?.[0] || "";
    const last = person?.user_last_name?.[0] || "";
    return `${first}${last}`.toUpperCase() || "?";
  };

  const selectedPlayers =
    playerdata?.filter((p) => PLayers.includes(p.player_id || p.user_id)) || [];
  const selectedCoaches = coachdata?.filter((c) => Coach.includes(c.coach_id)) || [];
  const coverSrc = newImagePreviewUrl || predata?.gr_image || "";
  const selectedMode = trainingModes.find((m) => m.te_gen_mode_id === selectedtraining);

  const renderPersonChip = (person, personId, onRemove, roleLabel) => (
    <span key={personId} className="eg-chip">
      {person?.user_image ? (
        <img className="eg-chip__avatar" src={person.user_image} alt="" />
      ) : (
        <span className="eg-chip__avatar eg-chip__avatar--fallback" aria-hidden="true">
          {personInitials(person)}
        </span>
      )}
      <span className="eg-chip__copy">
        <span className="eg-chip__name">{personName(person)}</span>
        <span className="eg-chip__role">{roleLabel}</span>
      </span>
      <button
        type="button"
        className="eg-chip__remove"
        aria-label={`${t("delete")} ${personName(person)}`}
        onClick={onRemove}
      >
        ×
      </button>
    </span>
  );

  return (
    <form className="eg-sheet" onSubmit={formik.handleSubmit}>
      <div className="eg-sheet__court">
        <div className="eg-sheet__tape" aria-hidden="true">
          <span>01</span>
        </div>
        <div className="eg-sheet__stage">
          <div className="eg-cover">
            {coverSrc ? (
              <img src={coverSrc} alt={formik.values.name || t("editgroup")} />
            ) : (
              <label htmlFor="profileimg" className="eg-cover__empty">
                <Camera size={28} strokeWidth={2.1} aria-hidden="true" />
                <span>{t("editgroup")}</span>
              </label>
            )}
            <label htmlFor="profileimg" className="eg-cover__btn">
              <Camera size={18} strokeWidth={2.2} aria-hidden="true" />
              <span className="eg-sr">{t("editgroup")}</span>
            </label>
          </div>
          <input
            className="eg-file"
            type="file"
            name="profileimg"
            id="profileimg"
            accept="image/*"
            onChange={handleFileInputChange}
          />
          <div className="eg-identity">
            <p className="eg-kicker">{t("editgroup")}</p>
            <label className="eg-sr" htmlFor="group-name">
              {t("name")}
            </label>
            <input
              id="group-name"
              className="eg-name"
              placeholder={t("writename")}
              type="text"
              name="name"
              value={formik.values.name || ""}
              onChange={formik.handleChange}
              autoComplete="off"
            />
            <label className="eg-sr" htmlFor="group-des">
              {t("description")}
            </label>
            <textarea
              id="group-des"
              className="eg-lede"
              placeholder={t("writegrp")}
              name="des"
              rows="3"
              value={formik.values.des || ""}
              onChange={formik.handleChange}
            />
          </div>
        </div>
      </div>

      <div className="eg-sheet__grid">
        <section
          className="eg-panel eg-panel--full"
          aria-labelledby="edit-group-kind-title"
        >
          <header className="eg-panel__head">
            <Table2 size={18} strokeWidth={2.2} aria-hidden="true" />
            <div>
              <h3 id="edit-group-kind-title">
                {t("groupKind", "Group type")}
              </h3>
              <p>
                {t(
                  "groupKindPanelHint",
                  "Training, championship, or Ping Santé"
                )}
              </p>
            </div>
          </header>
          <GroupKindFields
            kind={formik.values.kind}
            guestCoachName={formik.values.guestCoachName}
            onKind={(kind) => formik.setFieldValue("kind", kind)}
            onGuest={(name) => formik.setFieldValue("guestCoachName", name)}
          />
          {formik.values.kind === "ping_sante_prescription" ? (
            <PrescriptionHealthFields
              grId={id}
              players={(playerdata || []).filter((player) =>
                PLayers.includes(player.player_id)
              )}
            />
          ) : null}
        </section>

        <section
          className="eg-panel eg-panel--full"
          aria-labelledby="edit-group-join-title"
        >
          <header className="eg-panel__head">
            <ClipboardList size={18} strokeWidth={2.2} aria-hidden="true" />
            <div>
              <h3 id="edit-group-join-title">
                {t("groupJoinRulesTitle", "Assignment rules")}
              </h3>
              <p>
                {t(
                  "groupJoinRulesSubtitle",
                  "Who joins this group automatically at registration"
                )}
              </p>
            </div>
          </header>
          <GroupJoinRulesFields
            value={joinRulesForm}
            onChange={setJoinRulesForm}
          />
        </section>

        <section
          className="eg-panel eg-panel--full"
          aria-labelledby="edit-group-level-title"
        >
          <header className="eg-panel__head">
            <ClipboardList size={18} strokeWidth={2.2} aria-hidden="true" />
            <div>
              <h3 id="edit-group-level-title">{t("groupLevelTitle")}</h3>
              <p>{t("groupLevelSubtitle")}</p>
            </div>
          </header>
          <GroupLevelFields
            value={levelForm}
            onChange={setLevelForm}
            clubId={predata?.club_id}
          />
        </section>

        <section className="eg-panel" aria-labelledby="edit-group-players-title">
          <header className="eg-panel__head">
            <Users size={18} strokeWidth={2.2} aria-hidden="true" />
            <div>
              <h3 id="edit-group-players-title">{t("players")}</h3>
              <p>
                {PLayers.length} {t("selected")}
              </p>
            </div>
          </header>
          <button
            id="group-players"
            type="button"
            className="eg-picker"
            onClick={() => setPlayerDialogOpen(true)}
          >
            <span>
              {PLayers.length === 0
                ? t("selectPlayersForGroup")
                : `${PLayers.length} ${t("playersSelected")}`}
            </span>
            <span className="eg-picker__count">{PLayers.length}</span>
          </button>
          {selectedPlayers.length > 0 ? (
            <div className="eg-chips">
              {selectedPlayers.map((p) => {
                const pid = p.player_id || p.user_id;
                return renderPersonChip(
                  p,
                  pid,
                  () => setPlayers(PLayers.filter((itemId) => itemId !== pid)),
                  t("player")
                );
              })}
            </div>
          ) : null}
        </section>

        <section className="eg-panel" aria-labelledby="edit-group-coaches-title">
          <header className="eg-panel__head">
            <UserRound size={18} strokeWidth={2.2} aria-hidden="true" />
            <div>
              <h3 id="edit-group-coaches-title">{t("coach")}</h3>
              <p>
                {Coach.length} {t("selected")}
              </p>
            </div>
          </header>
          <Select
            id="group-coaches"
            multiple
            fullWidth
            displayEmpty
            value={Coach}
            onChange={(e) =>
              setCoach(
                typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : e.target.value
              )
            }
            input={<OutlinedInput notched={false} />}
            renderValue={(selected) => {
              if (!selected?.length) {
                return <span className="eg-placeholder">{t("coach")}</span>;
              }
              return `${selected.length} ${t("selected")}`;
            }}
            MenuProps={{ PaperProps: { className: "eg-menu" } }}
          >
            {coachdata?.map((name) => (
              <MenuItem
                key={name.coach_id}
                value={name.coach_id}
                className="eg-menu-item"
              >
                {personName(name)}
              </MenuItem>
            ))}
          </Select>
          {selectedCoaches.length > 0 ? (
            <div className="eg-chips">
              {selectedCoaches.map((c) =>
                renderPersonChip(
                  c,
                  c.coach_id,
                  () => setCoach(Coach.filter((itemId) => itemId !== c.coach_id)),
                  t("coach")
                )
              )}
            </div>
          ) : null}
        </section>

        <section className="eg-panel eg-panel--session" aria-labelledby="edit-group-session-title">
          <header className="eg-panel__head">
            <Table2 size={18} strokeWidth={2.2} aria-hidden="true" />
            <div>
              <h3 id="edit-group-session-title">{t("trainingMode")}</h3>
              <p>{selectedMode?.ts_gen_mode || t("selectTrainingMode")}</p>
            </div>
          </header>
          <div className="eg-session">
            <div className="eg-field">
              <label htmlFor="training-mode">{t("trainingMode")}</label>
              {trainingModes.length === 0 ? (
                <p className="eg-hint">{t("loadingTrainingModes") || t("loading")}</p>
              ) : (
                <Select
                  id="training-mode"
                  fullWidth
                  displayEmpty
                  value={selectedtraining || ""}
                  onChange={(e) => setselectedtraining(e.target.value || null)}
                  input={<OutlinedInput notched={false} />}
                  renderValue={(value) => {
                    if (!value) {
                      return <span className="eg-placeholder">{t("selectTrainingMode")}</span>;
                    }
                    const mode = trainingModes.find((m) => m.te_gen_mode_id === value);
                    return mode?.ts_gen_mode || value;
                  }}
                  MenuProps={{ PaperProps: { className: "eg-menu" } }}
                >
                  <MenuItem value="" className="eg-menu-item">
                    <em>{t("selectTrainingMode")}</em>
                  </MenuItem>
                  {trainingModes.map((mode) => (
                    <MenuItem
                      key={mode.te_gen_mode_id}
                      value={mode.te_gen_mode_id}
                      className="eg-menu-item"
                    >
                      {mode.ts_gen_mode}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </div>
            <div className="eg-field">
              <label htmlFor="number-of-tables">{t("numberOfTables")}</label>
              <div className="eg-stepper">
                <button
                  type="button"
                  className="eg-stepper__btn"
                  aria-label="-"
                  onClick={() => setNumberOfTables((n) => Math.max(1, Number(n) - 1))}
                >
                  <Minus size={16} strokeWidth={2.4} aria-hidden="true" />
                </button>
                <input
                  id="number-of-tables"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={numberOfTables}
                  onChange={(e) =>
                    setNumberOfTables(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                />
                <button
                  type="button"
                  className="eg-stepper__btn"
                  aria-label="+"
                  onClick={() => setNumberOfTables((n) => Math.max(1, Number(n) + 1))}
                >
                  <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="eg-dock">
        <button className="eg-save" type="submit">
          {t("saveChanges")}
        </button>
      </div>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={closealert}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert variant="filled" severity={alertSeverity}>
          {success}
        </Alert>
      </Snackbar>

      <PlayerSelectionDialog
        open={playerDialogOpen}
        onClose={() => setPlayerDialogOpen(false)}
        players={playerdata || []}
        selectedPlayers={PLayers}
        onSelectPlayers={handlePlayersSelected}
        confirmDisabled={savingPlayers}
        confirmLabel={
          savingPlayers
            ? t("saving") || "Saving…"
            : t("confirm") || "Confirm"
        }
        clubId={predata?.club_id}
      />
    </form>
  );
};
